import React, { useEffect, useState, useContext } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "../../profileAdminCard/adminModals.css";
import "./staffServicesModal.css";
import { Home, Monitor, Check } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";

const StaffServicesModal = ({ isOpen, onClose, shopId, shopServices = [], staffServices = [], onSave }) => {
	useModalDismiss(onClose, { enabled: isOpen });
	const { language, userData, setUserData } = useContext(CombinedContext);
	const [rows, setRows] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		if (!shopServices || shopServices.length === 0) return;

		const staffSet = new Set(staffServices);

		const mapped = shopServices.map((svc) => ({
			serviceId: svc.serviceId,
			name: svc.name,
			price: svc.price,
			currency: svc.currency,
			duration: svc.duration,
			isHomeService: svc.isHomeService,
			locationType: svc.locationType || (svc.isHomeService ? "home" : "in_shop"),
			active: staffSet.has(svc.serviceId),
		}));

		setRows(mapped);
	}, [isOpen, shopServices, staffServices]);

	useEffect(() => {
		if (!isOpen) setRows([]);
	}, [isOpen]);

	const toggleRow = (index) => {
		setRows((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], active: !next[index].active };
			return next;
		});
	};

	const selectedCount = rows.filter((r) => r.active).length;

	const handleSave = async () => {
		const serviceIds = rows.filter((r) => r.active).map((r) => r.serviceId);
		try {
			setSaving(true);
			const res = await companyBasedRequest.updateUserServices(
				userData.user.userId,
				serviceIds,
				userData.cognitoTokens.AccessToken
			);
			setUserData((prev) => ({ ...prev, user: res.user }));
			onSave?.(serviceIds);
			onClose();
		} catch (err) {
			console.error("Failed to save staff services", err);
		} finally {
			setSaving(false);
		}
	};

	if (!isOpen) return null;

	const t = translations[language];
	const svcT = t.profileStaff.services;

	const locationBadge = (row) => {
		if (row.locationType === "home") {
			return (
				<span className="sf-pm-svc-badge is-home">
					<Home size={12} strokeWidth={1.9} />
					{svcT.serviceType.home}
				</span>
			);
		}
		if (row.locationType === "online") {
			return (
				<span className="sf-pm-svc-badge is-online">
					<Monitor size={12} strokeWidth={1.9} />
					{svcT.serviceType.online}
				</span>
			);
		}
		return null;
	};

	return (
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal">
				{/* HEADER */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{svcT.servicesIOffer || svcT.title}</h2>
						<p className="sf-pm-sub">{svcT.intro}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} aria-label={t.calendar.editor.cancel}>
						✕
					</button>
				</div>

				{/* BODY */}
				<div className="sf-pm-body">
					<section className="sf-pm-section">
						<div className="sf-pm-label-row">
							<p className="sf-pm-label">{svcT.title}</p>
							{rows.length > 0 && (
								<span className="sf-pm-count">
									{selectedCount}/{rows.length}
								</span>
							)}
						</div>

						{rows.length === 0 ? (
							<p className="sf-pm-empty">
								{language === "es" ? "No hay servicios disponibles" : "No services available"}
							</p>
						) : (
							<div className="sf-pm-svc-list">
								{rows.map((row, idx) => (
									<button
										key={row.serviceId}
										type="button"
										aria-pressed={row.active}
										className={`sf-pm-svc ${row.active ? "is-active" : ""}`}
										onClick={() => toggleRow(idx)}
									>
										<span className="sf-pm-svc-check" aria-hidden="true">
											<Check size={13} strokeWidth={3} />
										</span>

										<span className="sf-pm-svc-info">
											<span className="sf-pm-svc-name">{row.name}</span>
											{locationBadge(row)}
										</span>

										<span className="sf-pm-svc-pricing">
											<span className="sf-pm-svc-price">
												{row.currency || "DOP"} {row.price ?? 0}
											</span>
											<span className="sf-pm-svc-duration">{row.duration} min</span>
										</span>
									</button>
								))}
							</div>
						)}
					</section>
				</div>

				{/* FOOTER */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
						{t.calendar.editor.cancel}
					</button>
					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving ? t.calendar.editor.saving : t.calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default StaffServicesModal;
